import { Add } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid2,
  Paper,
  Typography,
} from "@mui/material";
import grey from "@mui/material/colors/grey";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { LazyList } from "@pimo/pimo-components";
import { PMPopUp } from "@pimo/pimo-components";
import { usePimoForm } from "@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import dayjs from "dayjs";
import { ChangeEvent, useCallback, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Configuration,
  ExtendedFieldError,
  Project,
  PROJECT_NAMES,
} from "vmd-types";
import { checkField, VIEW_NAMES } from "vmd-utils";

import {
  DistributionImportSummary,
  importDistributionApps,
} from "../../helpers/excel-importer";
import { DcRelevanceImportSection } from "./dc-relevance-import-section";
import ProjectRow from "./project-row";

export type OEManagerProps = {
  language?: string;
};

export const ManageOEsSettingsPage: PimoReactComponent<OEManagerProps> = ({
  language = "en",
}) => {
  const { setValue, formState } = usePimoForm<{
    projects?: Project[];
    configuration?: Configuration;
    validationErrors: Record<string, ExtendedFieldError>;
    enableSaveButton: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });
  const visibleProjectCount =
    formState.projects?.filter((project) => project.flag !== "delete").length ??
    0;
  const projectName =
    PROJECT_NAMES.find(
      (entry) =>
        entry.single === (formState.configuration?.projectName ?? "Project")
    ) ?? PROJECT_NAMES[0];
  const distributionItemName =
    formState.configuration?.itemName?.trim() || "DC System";

  const [openDelete, setOpenDelete] = useState(false);

  const [currentOeOnCreateEditDelete, setCurrentOeOnCreateEditDelete] =
    useState<string[] | undefined>(undefined);

  const [selectedProjectIndices, setSelectedProjectIndices] = useState<
    string[]
  >([]);

  const appsFileInputRef = useRef<HTMLInputElement>(null);
  const [appsFile, setAppsFile] = useState<File | null>(null);
  const [appsLoading, setAppsLoading] = useState(false);
  const [resetAppsData, setResetAppsData] = useState(false);
  const [appsResult, setAppsResult] =
    useState<DistributionImportSummary | null>(null);
  const [appsError, setAppsError] = useState<{
    message: string;
    details: string[];
  } | null>(null);

  const handleAppsFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = "";
    setAppsFile(file);
    setAppsResult(null);
    setAppsError(null);
  };

  const handleImportApps = async () => {
    if (!appsFile) return;
    setAppsLoading(true);
    setAppsResult(null);
    setAppsError(null);
    try {
      const summary = await importDistributionApps(appsFile, resetAppsData);
      setAppsResult(summary);
      setAppsFile(null);
      setResetAppsData(false);
      document.dispatchEvent(new CustomEvent("distribution-import:refresh"));
    } catch (error) {
      const typed = error as Error & { details?: string[] };
      setAppsError({ message: typed.message, details: typed.details ?? [] });
    } finally {
      setAppsLoading(false);
    }
  };

  const handleAppend = () => {
    const newProject: Project = {
      id: uuidv4(),
      name: "",
      customId: "",
      description: "",
      owner: "",
      spentBudget: 0,
      flag: "create",
    };

    setValue("projects", [...(formState.projects ?? []), newProject]);
    setValue("enableSaveButton", true);
    const element = document.getElementById("dummy-to-scroll-into");
    element?.scrollIntoView({ behavior: "smooth" });
  };

  const handleDelete = (projects: string[]) => {
    setCurrentOeOnCreateEditDelete(projects);
    setOpenDelete(true);
  };

  const handleConfirmDelete = () => {
    if (currentOeOnCreateEditDelete === undefined) {
      return;
    }

    setValue(
      "projects",
      formState.projects?.map((item) =>
        item.id && currentOeOnCreateEditDelete?.includes(item.id)
          ? { ...item, flag: "delete" }
          : item
      )
    );
    setValue("enableSaveButton", true);
    setOpenDelete(false);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setCurrentOeOnCreateEditDelete(undefined);
  };

  const handleProjectSelectionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setSelectedProjectIndices(
      isChecked ? (formState.projects?.map((p) => p.id) ?? []) : []
    );
  };

  const handleProjectFieldChange = useCallback(
    (id: string, fieldKey: string, value: unknown) => {
      const getFieldDefinition = (fieldName: string) => {
        return formState.configuration?.fieldsDefinition
          ?.filter((field) => field.purpose === "projectBasic")
          ?.find((f) => f.fieldName === fieldName && f.enabled === true);
      };
      const getFieldIndexUsingId = (fieldId: string) => {
        return formState.projects?.findIndex((p) => p.id === fieldId);
      };

      const index = getFieldIndexUsingId(id);
      if (index === -1 || index === undefined) return;

      const newErrors = { ...(formState.validationErrors ?? {}) } as Record<
        string,
        ExtendedFieldError
      >;

      const setFlagIfNeeded = () => {
        const p = formState.projects?.[index];
        if (p && p.flag !== "create" && p.flag !== "delete") {
          setValue(`projects.${index}.flag`, "update");
        }
      };

      const def = getFieldDefinition(fieldKey);

      switch (fieldKey) {
        case "name":
        case "customId":
        case "description":
        case "owner":
        case "freeTextFieldOne": {
          setValue(`projects.${index}.${fieldKey}`, value);

          checkField({
            iterableFieldName: `projects.{idx}.${fieldKey}`,
            idx: index,
            setErrors: (errors) => setValue("validationErrors", errors),
            errors: newErrors,
            value,
            tabContainingErrors: VIEW_NAMES[5],
          });

          setFlagIfNeeded();
          break;
        }
        case "approvedBudget": {
          const raw = value as string;
          setValue(`projects.${index}.approvedBudget`, raw);

          checkField({
            iterableFieldName: `projects.{idx}.approvedBudget`,
            idx: index,
            setErrors: (errors) => setValue("validationErrors", errors),
            errors: newErrors,
            value,
            tabContainingErrors: VIEW_NAMES[5],
          });

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "startDate": {
          const dateVal = value as
            dayjs.Dayjs | string | number | Date | null | undefined;
          setValue(`projects.${index}.startDate`, dateVal);

          let entry: ExtendedFieldError | undefined;
          if (!dateVal && def?.isMandatory) {
            entry = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[5],
            };
          } else if (dateVal && dayjs(dateVal).isValid() === false) {
            entry = {
              type: "invalid",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[5],
            };
          }

          if (entry) {
            newErrors[`projects.${index}.startDate`] = entry;
          } else {
            delete newErrors[`projects.${index}.startDate`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "endDate": {
          const dateVal = value as
            dayjs.Dayjs | string | number | Date | null | undefined;
          setValue(`projects.${index}.endDate`, dateVal);

          let newEntry: ExtendedFieldError | undefined;
          if (!dateVal && def?.isMandatory) {
            newEntry = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[5],
            };
          } else if (dateVal && !dayjs(dateVal).isValid()) {
            newEntry = {
              type: "invalid",
              message: "This is not a valid date",
              tabContainingErrors: VIEW_NAMES[5],
            };
          } else if (
            dateVal &&
            formState.projects?.[index]?.startDate &&
            dayjs(dateVal).isBefore(
              dayjs(formState.projects?.[index]?.startDate)
            )
          ) {
            newEntry = {
              type: "invalid",
              message: "End date should be after start date",
              tabContainingErrors: VIEW_NAMES[5],
            };
          }

          if (newEntry) {
            newErrors[`projects.${index}.endDate`] = newEntry;
          } else {
            delete newErrors[`projects.${index}.endDate`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        case "category":
        case "secondCategory": {
          const val = value as string;
          setValue(`projects.${index}.${fieldKey}`, val);

          if (!val && def?.isMandatory) {
            newErrors[`projects.${index}.${fieldKey}`] = {
              type: "required",
              message: "This field is required",
              tabContainingErrors: VIEW_NAMES[5],
            } as ExtendedFieldError;
          } else {
            delete newErrors[`projects.${index}.${fieldKey}`];
          }

          setValue("validationErrors", newErrors);
          setFlagIfNeeded();
          break;
        }
        default: {
          setValue(`projects.${index}.${fieldKey}`, value);
          setFlagIfNeeded();
          break;
        }
      }
      if (formState.enableSaveButton !== true) {
        setValue(`enableSaveButton`, true);
      }
    },
    [
      formState.validationErrors,
      formState.enableSaveButton,
      formState.configuration?.fieldsDefinition,
      formState.projects,
      setValue,
    ]
  );

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "calc(100vh - 83px)",
        overflowY: "auto",
      }}
    >
      <Typography
        sx={{
          fontSize: "24px",
          fontWeight: 500,
        }}
      >
        {getLocalizedMessage(language, "manageProjects.title") ||
          `Manage ${projectName.plural}`}
      </Typography>

      <Paper
        sx={{
          borderRadius: 3,
          boxShadow: "none",
          border: "1px solid",
          borderColor: grey[300],
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          flex: 0,
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
          {getLocalizedMessage(language, "manageProjects.appsUpload.title", {
            itemName: distributionItemName,
          }) || `OEs & ${distributionItemName} Upload`}
        </Typography>
        <Typography sx={{ fontSize: "14px", color: grey[700] }}>
          {getLocalizedMessage(
            language,
            "manageProjects.appsUpload.description",
            { itemName: distributionItemName }
          ) ||
            `Create OEs and ${distributionItemName} from one Excel file. Optionally reset existing OEs, ${distributionItemName} and channels before import.`}
        </Typography>

        <input
          ref={appsFileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleAppsFileSelected}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => appsFileInputRef.current?.click()}
            disabled={appsLoading}
            sx={{
              textTransform: "none",
              borderRadius: "4px",
              padding: "7px 16px",
              whiteSpace: "nowrap",
              minWidth: "fit-content",
            }}
          >
            {getLocalizedMessage(
              language,
              "manageProjects.appsUpload.upload"
            ) || "Upload"}
          </Button>

          {appsFile && (
            <Button
              variant="contained"
              onClick={handleImportApps}
              disabled={appsLoading}
              startIcon={<Add />}
              sx={{
                textTransform: "none",
                borderRadius: "4px",
                padding: "7px 16px",
                whiteSpace: "nowrap",
                minWidth: "fit-content",
                color: "#fff",
                "& .MuiButton-startIcon": { color: "#fff" },
              }}
            >
              {appsLoading
                ? getLocalizedMessage(
                    language,
                    "manageProjects.appsUpload.importing"
                  ) || "Importing…"
                : getLocalizedMessage(
                    language,
                    "manageProjects.appsUpload.import",
                    { itemName: distributionItemName }
                  ) || `Import OEs & ${distributionItemName}`}
            </Button>
          )}
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={resetAppsData}
              onChange={(e) => setResetAppsData(e.target.checked)}
              disabled={appsLoading}
            />
          }
          label={
            getLocalizedMessage(language, "manageProjects.appsUpload.reset", {
              itemName: distributionItemName,
            }) ||
            `Reset existing OEs, ${distributionItemName} and channels before import`
          }
          sx={{
            alignItems: "flex-start",
            m: 0,
            ".MuiFormControlLabel-label": {
              fontSize: "14px",
              color: grey[700],
              pt: "9px",
            },
          }}
        />

        <Typography sx={{ fontSize: "13px", color: grey[600] }}>
          {getLocalizedMessage(
            language,
            "manageProjects.appsUpload.resetHint",
            { itemName: distributionItemName }
          ) ||
            `Removes existing OEs, ${distributionItemName}, reporting links and channel assignments before importing.`}
        </Typography>

        {appsFile && !appsResult && !appsError && (
          <Typography sx={{ fontSize: "14px", color: grey[700] }}>
            {appsFile.name}
          </Typography>
        )}

        {appsResult && (
          <Typography sx={{ fontSize: "14px", color: grey[700] }}>
            {getLocalizedMessage(
              language,
              "manageProjects.appsUpload.success",
              {
                oes: appsResult.oesCreated,
                channels: appsResult.channelsCreated,
                businessApps: appsResult.businessApps,
              }
            ) ||
              `Created ${appsResult.oesCreated} OEs, ${appsResult.channelsCreated} channels, ${appsResult.businessApps} DC Business Apps. Reload the report to see them.`}
            {appsResult.skippedRows > 0 &&
              ` ${
                getLocalizedMessage(
                  language,
                  "manageProjects.appsUpload.skipped",
                  { skipped: appsResult.skippedRows }
                ) ||
                `${appsResult.skippedRows} rows without a valid OE were skipped.`
              }`}
          </Typography>
        )}

        {appsError && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              sx={{ fontSize: "14px", color: "error.main", fontWeight: 500 }}
            >
              {appsError.message}
            </Typography>
            {appsError.details.map((detail, index) => (
              <Typography
                key={index}
                sx={{ fontSize: "13px", color: "error.main" }}
              >
                {`• ${detail}`}
              </Typography>
            ))}
          </Box>
        )}
      </Paper>

      <DcRelevanceImportSection
        language={language}
        projects={(formState.projects ?? []).filter(
          (project) => project.flag !== "create" && project.flag !== "delete"
        )}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          mt: 2,
          flex: 1,
          minHeight: 320,
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 500,
          }}
        >
          {getLocalizedMessage(language, "manageProjects.listTitle") ||
            `${projectName.plural} List`}{" "}
          ({visibleProjectCount} {projectName.plural})
        </Typography>
        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: "none",
            border: "1px solid",
            borderColor: grey[300],
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Grid2
            container
            alignItems="center"
            sx={{
              height: 65,
              p: 2,
              borderBottom: "1px solid",
              borderColor: grey[300],
              flex: 0,
            }}
          >
            <Grid2 size={0.5}>
              <Checkbox
                onChange={handleProjectSelectionChange}
                icon={
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      border: "2px solid",
                      borderColor: grey[700],

                      display: "inline-block",
                      backgroundColor: "#fff",
                    }}
                  />
                }
                checkedIcon={
                  <span
                    style={{
                      width: 20,
                      height: 20,

                      border: "2px solid",
                      borderColor: grey[700],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#fff",
                    }}
                  >
                    <CheckIcon
                      style={{
                        fontSize: 18,
                        color: grey[700],
                      }}
                    />
                  </span>
                }
                checked={
                  selectedProjectIndices?.length === formState.projects?.length
                }
              />
            </Grid2>
            <Grid2 size={8.5} sx={{ fontSize: "14px", fontWeight: 500 }}>
              {getLocalizedMessage(language, "manageProjects.projectName") ||
                `${projectName.single} Name`}
            </Grid2>
            <Grid2
              size={3}
              sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
            >
              {selectedProjectIndices?.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => handleDelete(selectedProjectIndices)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "4px",
                    padding: "7px 16px",
                    whiteSpace: "nowrap",
                    minWidth: "fit-content",
                  }}
                >
                  {getLocalizedMessage(language, "deleteSelected") ||
                    "Delete Selected"}
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={handleAppend}
                sx={{
                  textTransform: "none",
                  borderRadius: "4px",
                  padding: "7px 16px",
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                }}
                startIcon={<Add />}
              >
                {getLocalizedMessage(language, "addProjects", {
                  projectName: projectName.single,
                }) || `Add ${projectName.single}`}
              </Button>
            </Grid2>
          </Grid2>

          <LazyList
            itemCount={visibleProjectCount}
            // Render every OE up front: the list is small (tens of entries), and
            // scroll-driven paging only strands rows if the scroll container ever
            // stops being this box.
            itemsPerPage={Math.max(visibleProjectCount, 1)}
            sx={{
              width: "100%",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            {(start, end) => (
              <>
                {formState.projects
                  ?.filter((project) => project.flag !== "delete")
                  .slice(start, end)
                  .map((field) => (
                    <ProjectRow
                      key={field.id}
                      field={field}
                      formState={formState}
                      setValue={setValue}
                      handleDelete={handleDelete}
                      selectedProjectIndices={selectedProjectIndices}
                      setSelectedProjectIndices={setSelectedProjectIndices}
                      projectName={projectName}
                      onFieldChange={handleProjectFieldChange}
                      language={language}
                    />
                  ))}
                {(formState.projects?.length ?? 0) > 6 &&
                  end ===
                    (formState.projects?.filter(
                      (project) => project.flag !== "delete"
                    ).length ?? 0) && (
                    <Box sx={{ height: 40 }} id={"dummy-to-scroll-into"}></Box>
                  )}
              </>
            )}
          </LazyList>
        </Paper>

        <PMPopUp
          open={openDelete}
          title={
            getLocalizedMessage(
              language || "en",
              "dialog.title.confirmDelete"
            ) || "Confirm Delete"
          }
          description={
            currentOeOnCreateEditDelete?.length === 1
              ? getLocalizedMessage(
                  language || "en",
                  "dialog.message.genericConfirm",
                  {
                    itemName: projectName.single,
                  }
                ) ||
                `Are you sure you want to delete this ${projectName.single}?`
              : getLocalizedMessage(
                  language || "en",
                  "dialog.message.genericConfirmMulti",
                  {
                    itemCount: currentOeOnCreateEditDelete?.length ?? 0,
                    itemName: projectName.plural,
                  }
                ) ||
                `Are you sure you want to delete these ${currentOeOnCreateEditDelete?.length} ${projectName.plural}?`
          }
          onClose={() => handleCloseDelete()}
          maxWidth="sm"
          cancelButtonText={
            getLocalizedMessage(language || "en", "dialog.button.cancel") ||
            "Cancel"
          }
          cancelButtonFireEvent={() => handleCloseDelete()}
          cancelButtonColor="white"
          confirmationButtonColor="error"
          confirmationButtonText={
            getLocalizedMessage(language || "en", "dialog.button.delete") ||
            "Delete"
          }
          confirmationButtonFireEvent={() => {
            handleConfirmDelete();
          }}
        />
      </Box>
    </Box>
  );
};
